/*
 * description: 根布局，页面标题，描述等信息
 * */

import React, { useEffect, useRef } from 'react'
import { connect } from 'dva'
import { Result, Button } from 'antd'
import moment from 'moment'

import FrownOutlined from '@ant-design/icons/FrownOutlined'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import * as Sentry from '@sentry/react'

import { checkBrowser } from '@/components/BrowserCompatibilityTip'
import { setReferrerUrl } from '@/utils/utils'
import AgentLoginAndPreBar from '@/components/GlobalHelper/AgentLoginAndPreBar'
import useLocationDoc from '@/modules/resource/hooks/useLocationDoc'
import { captureException } from '@/utils/track'
import { isTtDaHuoApp } from '@/utils/singleApp'

import styles from './index.less'

const wrapStyle = {
    width: '70vw',
    marginTop: '10vh',
    marginLeft: '15vw'
}

const FallbackComponent = props => {
    const allowed = checkBrowser()
    const { error, componentStack } = props

    const eventId = captureException(
        `环境:${window.PROJECT_CONFIG.sentryMode || window.PROJECT_CONFIG.mode}应用发生问题，请刷新页面或联系客服。${
            error.message
        }`,
        {
            anchor: 'root layout FallbackComponent',
            extra: {
                error
            },
            mustNotify: true
        }
    )

    if (PROJECT_CONFIG.mode === 'prod') {
        return (
            <div className={styles.fallback}>
                <Result
                    icon={<FrownOutlined />}
                    title={<div>应用发生问题，请刷新页面或联系客服。</div>}
                    extra={
                        <div>
                            {!allowed && (
                                <div>
                                    发现您的浏览器版本过低，请下载最新
                                    <a href="https://www.google.cn/intl/zh-CN/chrome">谷歌浏览器</a>
                                    访问应用
                                </div>
                            )}

                            <div className={styles.mt20}>
                                <Button type="link" href="/">
                                    返回首页
                                </Button>
                            </div>
                            <div className={styles.eventId}>
                                {moment().format('YYYY-MM-DD HH:mm:ss')} {eventId}
                            </div>
                        </div>
                    }
                />
            </div>
        )
    }

    return (
        <div style={wrapStyle}>
            <h1>应用发生问题，请火速联系前端</h1>
            <div>
                <pre> {error.message}</pre>
            </div>
            <div>
                <pre>{componentStack}</pre>
            </div>
        </div>
    )
}

const Layout = ({ children, settings, location = { pathname: '' } }) => {
    useLocationDoc({ location })

    const { favicon, title, keywords, description, domainUrl } = settings

    // 临时记录路由变化的路径
    const pathTempDataRef = useRef('')
    useEffect(() => {
        if (pathTempDataRef.current) {
            // 设置window.referrerUrl
            setReferrerUrl(pathTempDataRef.current)
        }
        pathTempDataRef.current = location.pathname
    }, [location.pathname])

    // TODO hack：Helmet 不生效
    const faviconEl = document.querySelector('link[rel=icon]')

    // 寻合作四个页面(ttdahuo)网页Tab标题去掉聚水潭图标和文字
    if (isTtDaHuoApp) {
        faviconEl &&
            faviconEl.setAttribute(
                'href',
                'https://assets.sursung.com/activity/20240529/51AA7ABCC0D64D6C8A4533B8F4EFDD941716966342698.ico'
            )
    } else {
        faviconEl && faviconEl.setAttribute('href', favicon)
    }

    const renderMeta = () => {
        if (isTtDaHuoApp) {
            return null
        }

        return (
            <>
                <meta property="og:title" content={title} />
                <meta property="og:url" content={domainUrl} />
                <meta property="og:image" content={favicon} />
                <meta property="og:description" content={description} />
                <meta property="og:site_name" content={title} />
                <meta name="keywords" content={keywords} />
                <link rel="icon" type="image/x-icon" href={favicon} />
            </>
        )
    }

    return (
        <HelmetProvider>
            <Helmet>{renderMeta()}</Helmet>

            <Sentry.ErrorBoundary fallback={FallbackComponent}>
                {window.__POWERED_BY_GYL_FRAME__ ? null : <AgentLoginAndPreBar />}
                {children}
            </Sentry.ErrorBoundary>
        </HelmetProvider>
    )
}

export default connect(({ settings }) => ({
    settings
}))(Sentry.withProfiler(Layout))
